import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Loader2, MapPin, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, '')}/api` : '/api';

export const Checkout = () => {
  const navigate = useNavigate();
  const { cart, cartTotal, fetchCart } = useCart();
  const { user } = useAuth();
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState('delivery');
  const [showAddressForm, setShowAddressForm] = useState(true);
  const [shipping, setShipping] = useState(null);
  const [loading, setLoading] = useState(false);
  const [addressForm, setAddressForm] = useState({
    name: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    zipcode: '',
    phone: ''
  });
  const [loadingCep, setLoadingCep] = useState(false);
  const [cepFound, setCepFound] = useState(false);

  const [couponCode, setCouponCode] = useState('');
  const [couponApplying, setCouponApplying] = useState(false);
  const [couponDiscount, setCouponDiscount] = useState(0);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }
    if (!cart.items || cart.items.length === 0) {
      navigate('/cart');
      return;
    }
    fetchAddresses();
  }, [user, cart]);

  const fetchAddresses = async () => {
    try {
      const response = await axios.get(`${API}/addresses`, { withCredentials: true });
      const list = Array.isArray(response.data) ? response.data : [];
      setAddresses(list);

      // Se ainda não há endereço selecionado, seleciona o padrão (se existir) ou o primeiro.
      if (!selectedAddress) {
        const defaultAddr = list.find(a => a.is_default);
        if (defaultAddr) {
          setSelectedAddress(defaultAddr.id);
        } else if (list.length > 0) {
          setSelectedAddress(list[0].id);
        }
      }
    } catch (error) {
      console.error('Error fetching addresses:', error);
    }
  };

  // Format CEP: 00000-000
  const formatCep = (value) => {
    const numbers = value.replace(/\D/g, '').slice(0, 8);
    if (numbers.length <= 5) return numbers;
    return `${numbers.slice(0, 5)}-${numbers.slice(5)}`;
  };

  // Format phone: (00) 00000-0000
  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '').slice(0, 11);
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
  };

  // Fetch address from ViaCEP API
  const fetchAddressFromCep = async (cep) => {
    const cleanCep = cep.replace(/\D/g, '');
    if (cleanCep.length !== 8) {
      setCepFound(false);
      return;
    }
    
    setLoadingCep(true);
    setCepFound(false);
    
    try {
      const response = await axios.get(`https://viacep.com.br/ws/${cleanCep}/json/`);
      
      if (response.data.erro) {
        toast.error('CEP não encontrado');
        setCepFound(false);
      } else {
        setAddressForm(prev => ({
          ...prev,
          street: response.data.logradouro || '',
          neighborhood: response.data.bairro || '',
          city: response.data.localidade || '',
          state: response.data.uf || ''
        }));
        setCepFound(true);
        toast.success('Endereço encontrado!');
      }
    } catch (error) {
      console.error('Error fetching CEP:', error);
      toast.error('Erro ao buscar CEP');
      setCepFound(false);
    } finally {
      setLoadingCep(false);
    }
  };

  const handleCepChange = (e) => {
    const formatted = formatCep(e.target.value);
    setAddressForm({ ...addressForm, zipcode: formatted });
    
    // Auto-fetch when CEP is complete
    if (formatted.replace(/\D/g, '').length === 8) {
      fetchAddressFromCep(formatted);
    } else {
      setCepFound(false);
    }
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    setAddressForm({ ...addressForm, phone: formatted });
  };

  const handleAddAddress = async (e) => {
    e.preventDefault();
    
    // Validations
    if (addressForm.name.length < 3) {
      toast.error('Nome deve ter pelo menos 3 caracteres');
      return;
    }
    if (addressForm.zipcode.replace(/\D/g, '').length !== 8) {
      toast.error('CEP inválido');
      return;
    }
    if (addressForm.street.length < 3) {
      toast.error('Rua deve ter pelo menos 3 caracteres');
      return;
    }
    if (!addressForm.number) {
      toast.error('Número é obrigatório');
      return;
    }
    if (addressForm.neighborhood.length < 2) {
      toast.error('Bairro deve ter pelo menos 2 caracteres');
      return;
    }
    if (addressForm.city.length < 2) {
      toast.error('Cidade deve ter pelo menos 2 caracteres');
      return;
    }
    if (addressForm.state.length !== 2) {
      toast.error('Estado deve ter 2 caracteres (ex: SP)');
      return;
    }
    if (addressForm.phone.replace(/\D/g, '').length < 10) {
      toast.error('Telefone inválido');
      return;
    }

    try {
      const created = await axios.post(`${API}/addresses`, addressForm, { withCredentials: true });
      toast.success('Endereço adicionado!');
      const createdId = created?.data?.id;
      await fetchAddresses();
      if (createdId) {
        setSelectedAddress(createdId);
      }
      setShowAddressForm(false);
      setCepFound(false);
      setAddressForm({
        name: '', street: '', number: '', complement: '',
        neighborhood: '', city: '', state: '', zipcode: '', phone: ''
      });
    } catch (error) {
      toast.error('Erro ao adicionar endereço');
    }
  };

  const calculateShipping = async () => {
    const addr = addresses.find(a => a.id === selectedAddress);
    if (!addr) return;
    try {
      const response = await axios.post(`${API}/shipping/calculate?zipcode=${addr.zipcode}`);
      setShipping(response.data.services[0]);
    } catch (error) {
      console.error('Error calculating shipping:', error);
    }
  };

  useEffect(() => {
    if (deliveryMethod === 'pickup') {
      setShipping({ price: 0, name: 'Retirada', days: 0 });
      return;
    }
    if (selectedAddress) {
      calculateShipping();
    }
  }, [selectedAddress, deliveryMethod]);

  const handleApplyCoupon = async () => {
    const code = couponCode.trim().toUpperCase();
    if (!code) {
      toast.error('Informe o código do cupom');
      return;
    }

    const totalBeforeDiscount = cartTotal + (shipping?.price || 0);

    setCouponApplying(true);
    try {
      const resp = await axios.post(
        `${API}/coupons/validate`,
        { code, total: totalBeforeDiscount },
        { withCredentials: true }
      );
      const discount = Number(resp?.data?.discount || 0);
      if (!isFinite(discount) || discount <= 0) {
        toast.error('Cupom inválido');
        setCouponDiscount(0);
        return;
      }
      setCouponDiscount(discount);
      setCouponCode(code);
      toast.success('Cupom aplicado!');
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Erro ao validar cupom');
      setCouponDiscount(0);
    } finally {
      setCouponApplying(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponCode('');
    setCouponDiscount(0);
    toast.message('Cupom removido');
  };

  const handlePlaceOrder = async () => {
    if (deliveryMethod === 'delivery' && !selectedAddress) {
      toast.error('Selecione um endereço de entrega');
      return;
    }
    setLoading(true);
    try {
      // Sincroniza o carrinho antes de criar o pedido (evita estado local desatualizado)
      await fetchCart();
      if (!cart.items || cart.items.length === 0) {
        toast.error('Seu carrinho está vazio');
        setLoading(false);
        navigate('/cart');
        return;
      }

      console.log('[checkout] creating order', { address_id: selectedAddress, payment_method: 'mercadopago', delivery_method: deliveryMethod });
      const orderResponse = await axios.post(
        `${API}/orders`,
        {
          address_id: selectedAddress,
          payment_method: 'mercadopago',
          delivery_method: deliveryMethod,
          coupon_code: couponCode.trim().toUpperCase() || null,
        },
        { withCredentials: true, timeout: 20000 }
      );
      
      const order = orderResponse.data;

      if (!order?.id) {
        console.error('[checkout] invalid order response', order);
        toast.error('Erro ao criar pedido (resposta inválida)');
        setLoading(false);
        return;
      }
      
      console.log('[checkout] creating mercadopago session', { order_id: order.id, origin_url: window.location.origin });
      const checkoutResponse = await axios.post(
        `${API}/checkout/session`,
        { order_id: order.id, origin_url: window.location.origin },
        { withCredentials: true, timeout: 20000 }
      );
      
      const url = checkoutResponse?.data?.url;
      if (!url) {
        console.error('[checkout] invalid checkout session response', checkoutResponse?.data);
        toast.error('Erro ao obter link do Mercado Pago');
        setLoading(false);
        return;
      }

      // Redirect to Mercado Pago
      window.location.href = url;
    } catch (error) {
      console.error('[checkout] error placing order', error);
      const status = error?.response?.status;
      const data = error?.response?.data;
      console.error('[checkout] error response', { status, data });
      const detail = data?.detail;
      const code = error?.code;
      if (code === 'ECONNABORTED') {
        toast.error('Tempo excedido ao comunicar com o servidor. Tente novamente.');
      } else {
      toast.error(detail || (status ? `Erro ao finalizar pedido (HTTP ${status})` : 'Erro ao finalizar pedido'));
      }
      setLoading(false);
    }
  };

  const totalWithShipping = cartTotal + (shipping?.price || 0);
  const discountApplied = couponDiscount > 0 ? Math.min(couponDiscount, totalWithShipping) : 0;
  const finalTotal = Math.max(0, totalWithShipping - discountApplied);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0f172a] pt-24 pb-12">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl font-bold text-slate-900 dark:text-white mb-8">Finalizar Compra</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="bg-white dark:bg-slate-800 p-6 rounded-xl shadow-lg">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">Endereço de Entrega</h2>

              <div className="mb-6">
                <RadioGroup value={deliveryMethod} onValueChange={setDeliveryMethod} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className={`flex items-center space-x-2 p-4 rounded-xl border ${deliveryMethod === 'delivery' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-slate-200 dark:border-slate-700'}`}>
                    <RadioGroupItem value="delivery" id="delivery" />
                    <Label htmlFor="delivery" className="font-medium">Entregar</Label>
                  </div>
                  <div className={`flex items-center space-x-2 p-4 rounded-xl border ${deliveryMethod === 'pickup' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-slate-200 dark:border-slate-700'}`}>
                    <RadioGroupItem value="pickup" id="pickup" />
                    <Label htmlFor="pickup" className="font-medium">Retirar</Label>
                  </div>
                </RadioGroup>
              </div>

              {deliveryMethod === 'delivery' && addresses.length > 0 ? (
                <div className="space-y-4 mb-6">
                  <p className="text-sm text-slate-600 dark:text-slate-400">Endereços salvos:</p>
                  <RadioGroup value={selectedAddress} onValueChange={setSelectedAddress} className="space-y-3">
                    {addresses.map((address) => (
                      <div 
                        key={address.id} 
                        className={`p-4 rounded-xl border cursor-pointer transition-all ${
                          selectedAddress === address.id
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                        }`}
                      >
                        <div className="flex items-start space-x-3">
                          <RadioGroupItem value={address.id} id={address.id} className="mt-1" />
                          <Label htmlFor={address.id} className="cursor-pointer flex-1">
                            <p className="font-semibold text-slate-900 dark:text-white">{address.name}</p>
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                              {address.street}, {address.number} - {address.neighborhood}
                            </p>
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                              {address.city} - {address.state}
                            </p>
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                              CEP: {address.zipcode}
                            </p>
                            <p className="text-sm text-slate-600 dark:text-slate-400">
                              Tel: {address.phone}
                            </p>
                          </Label>
                        </div>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              ) : deliveryMethod === 'delivery' ? (
                <p className="text-slate-600 dark:text-slate-400 mb-6">Nenhum endereço salvo. Adicione um novo endereço abaixo.</p>
              ) : (
                <p className="text-slate-600 dark:text-slate-400 mb-6">Você escolheu retirar. O frete será R$ 0,00.</p>
              )}

              {deliveryMethod === 'delivery' && (
              <div className="border-t border-slate-200 dark:border-slate-700 pt-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white">Novo Endereço</h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowAddressForm(!showAddressForm)}
                    data-testid="toggle-address-form"
                  >
                    {showAddressForm ? 'Ocultar' : 'Mostrar'}
                  </Button>
                </div>
                <form onSubmit={handleAddAddress} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Nome Completo */}
                    <div>
                      <Label className="text-sm font-medium">Nome Completo *</Label>
                      <Input 
                        value={addressForm.name} 
                        onChange={(e) => setAddressForm({...addressForm, name: e.target.value})} 
                        placeholder="Ex: João da Silva"
                        minLength={3}
                        maxLength={100}
                        required 
                      />
                      <p className="text-xs text-slate-500 mt-1">Mínimo 3 caracteres</p>
                    </div>
                    
                    {/* CEP com busca automática */}
                    <div>
                      <Label className="text-sm font-medium">CEP *</Label>
                      <div className="relative">
                        <Input 
                          value={addressForm.zipcode} 
                          onChange={handleCepChange}
                          placeholder="00000-000"
                          maxLength={9}
                          required 
                          className={cepFound ? 'border-green-500 pr-10' : ''}
                        />
                        {loadingCep && (
                          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-cyan-500" />
                        )}
                        {cepFound && !loadingCep && (
                          <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">Digite o CEP para buscar o endereço</p>
                    </div>
                    
                    {/* Rua */}
                    <div>
                      <Label className="text-sm font-medium">Rua *</Label>
                      <Input 
                        value={addressForm.street} 
                        onChange={(e) => setAddressForm({...addressForm, street: e.target.value})} 
                        placeholder="Ex: Rua das Flores"
                        minLength={3}
                        maxLength={200}
                        required 
                        className={cepFound ? 'bg-slate-50 dark:bg-slate-700' : ''}
                      />
                    </div>
                    
                    {/* Número */}
                    <div>
                      <Label className="text-sm font-medium">Número *</Label>
                      <Input 
                        value={addressForm.number} 
                        onChange={(e) => setAddressForm({...addressForm, number: e.target.value.replace(/\D/g, '').slice(0, 10)})} 
                        placeholder="Ex: 123"
                        maxLength={10}
                        required 
                      />
                    </div>
                    
                    {/* Complemento */}
                    <div>
                      <Label className="text-sm font-medium">Complemento</Label>
                      <Input 
                        value={addressForm.complement} 
                        onChange={(e) => setAddressForm({...addressForm, complement: e.target.value})} 
                        placeholder="Ex: Apto 101, Bloco A"
                        maxLength={100}
                      />
                      <p className="text-xs text-slate-500 mt-1">Opcional</p>
                    </div>
                    
                    {/* Bairro */}
                    <div>
                      <Label className="text-sm font-medium">Bairro *</Label>
                      <Input 
                        value={addressForm.neighborhood} 
                        onChange={(e) => setAddressForm({...addressForm, neighborhood: e.target.value})} 
                        placeholder="Ex: Centro"
                        minLength={2}
                        maxLength={100}
                        required 
                        className={cepFound ? 'bg-slate-50 dark:bg-slate-700' : ''}
                      />
                    </div>
                    
                    {/* Cidade */}
                    <div>
                      <Label className="text-sm font-medium">Cidade *</Label>
                      <Input 
                        value={addressForm.city} 
                        onChange={(e) => setAddressForm({...addressForm, city: e.target.value})} 
                        placeholder="Ex: São Paulo"
                        minLength={2}
                        maxLength={100}
                        required 
                        className={cepFound ? 'bg-slate-50 dark:bg-slate-700' : ''}
                      />
                    </div>
                    
                    {/* Estado */}
                    <div>
                      <Label className="text-sm font-medium">Estado *</Label>
                      <Input 
                        value={addressForm.state} 
                        onChange={(e) => setAddressForm({...addressForm, state: e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2)})} 
                        placeholder="Ex: SP"
                        maxLength={2}
                        required 
                        className={cepFound ? 'bg-slate-50 dark:bg-slate-700' : ''}
                      />
                      <p className="text-xs text-slate-500 mt-1">2 letras (ex: SP, RJ, MG)</p>
                    </div>
                    
                    {/* Telefone */}
                    <div className="md:col-span-2">
                      <Label className="text-sm font-medium">Telefone *</Label>
                      <Input 
                        value={addressForm.phone} 
                        onChange={handlePhoneChange}
                        placeholder="(00) 00000-0000"
                        maxLength={15}
                        required 
                      />
                      <p className="text-xs text-slate-500 mt-1">Celular com DDD</p>
                    </div>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full md:w-auto bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500"
                    data-testid="save-address-btn"
                  >
                    <MapPin className="h-4 w-4 mr-2" />
                    Salvar Endereço
                  </Button>
                </form>
            </div>
              )}
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-slate-800 p-6 rounded-xl shadow-lg sticky top-24">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">Resumo do Pedido</h2>

              <div className="mb-6 space-y-2">
                <Label className="text-sm font-medium">Cupom</Label>
                <div className="flex gap-2">
                  <Input
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    placeholder="EX: DESCONTO10"
                    className="flex-1"
                    disabled={couponApplying}
                  />
                  {discountApplied > 0 ? (
                    <Button type="button" variant="outline" onClick={handleRemoveCoupon} disabled={couponApplying}>
                      Remover
                    </Button>
                  ) : (
                    <Button type="button" onClick={handleApplyCoupon} disabled={couponApplying || !couponCode.trim()}>
                      {couponApplying ? 'Aplicando...' : 'Aplicar'}
                    </Button>
                  )}
                </div>
                {discountApplied > 0 && (
                  <p className="text-xs text-green-600 dark:text-green-400">
                    Cupom aplicado. Desconto: R$ {discountApplied.toFixed(2)}
                  </p>
                )}
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between">
                  <span className="text-slate-600 dark:text-slate-400">Subtotal</span>
                  <span className="font-semibold">R$ {cartTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600 dark:text-slate-400">Frete</span>
                  <span className="font-semibold">
                    {shipping ? `R$ ${shipping.price.toFixed(2)}` : 'Calculando...'}
                  </span>
                </div>
                {discountApplied > 0 && (
                  <div className="flex justify-between">
                    <span className="text-slate-600 dark:text-slate-400">Desconto</span>
                    <span className="font-semibold text-green-600 dark:text-green-400">- R$ {discountApplied.toFixed(2)}</span>
                  </div>
                )}
                <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
                  <div className="flex justify-between text-xl font-bold">
                    <span>Total</span>
                    <span className="text-blue-600 dark:text-blue-400" data-testid="final-total">
                      R$ {finalTotal.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
              <Button
                className="w-full"
                size="lg"
                onClick={handlePlaceOrder}
                disabled={loading || (deliveryMethod === 'delivery' && !selectedAddress)}
                data-testid="place-order-btn"
              >
                {loading ? 'Processando...' : 'Finalizar Pedido'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};